import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import './EMSATabsContainer.css';

/**
 * @summary Custom tabs container for EMSA Training Programs
 * @param {array} tabs - Array of tab configuration objects
 * @param {function} onTabChange - Callback function when tab changes
 * @returns {React.ReactElement} Returns the EMSATabsContainer component
 */
const EMSATabsContainer = ({ tabs, onTabChange }) => {
  const [activeTab, setActiveTab] = useState(0);
  const prevTabsRef = useRef(null);
  const isInitialMount = useRef(true);

  // Reset to first tab if tabs change significantly (like when data loads)
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Only reset if tabs have changed in a meaningful way
    const prevTabsLength = prevTabsRef.current?.length || 0;
    if (tabs && tabs.length > 0 && prevTabsLength !== tabs.length) {
      // console.log('Tabs count changed from', prevTabsLength, 'to', tabs.length);
      setActiveTab(0);
      if (onTabChange) {
        onTabChange(0);
      }
    }
    
    // Update the ref with current tabs
    prevTabsRef.current = tabs;
  }, [tabs, onTabChange]);

  const handleTabClick = useCallback((index) => {
    // console.log('Tab clicked:', index);
    setActiveTab(index);
    if (onTabChange) {
      onTabChange(index);
    }
  }, [onTabChange]);

  const handleTabKeyDown = useCallback((e, currentIndex) => {
    let newIndex = currentIndex;

    if (e.key === 'ArrowRight') {
      newIndex = (currentIndex + 1) % tabs.length;
    } else if (e.key === 'ArrowLeft') {
      newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (e.key === 'Home') {
      newIndex = 0;
    } else if (e.key === 'End') {
      newIndex = tabs.length - 1;
    } else {
      return; // Not a navigation key
    }

    e.preventDefault();
    setActiveTab(newIndex);
    if (onTabChange) {
      onTabChange(newIndex);
    }
    // Focus the new tab
    setTimeout(() => {
      document.getElementById(`tab-${newIndex}`)?.focus();
    }, 0);
  }, [tabs.length, onTabChange]);

  return (
    <div className="emsa-tabs-container">
      <div className="emsa-tabs-header" role="tablist" aria-label="Training program categories">
        <ul>
          {tabs.map((tab, index) => (
            <li
              key={`tab-${index}`}
              className={activeTab === index ? 'active' : ''}
            >
              <button
                onClick={() => handleTabClick(index)}
                onKeyDown={(e) => handleTabKeyDown(e, index)}
                aria-selected={activeTab === index}
                role="tab"
                id={`tab-${index}`}
                aria-controls={`tabpanel-${index}`}
                tabIndex={activeTab === index ? 0 : -1}
                title={tab.label}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>
      </div>
      <div className="emsa-tabs-content">
        {tabs.map((tab, index) => (
          <div
            key={`tabpanel-${index}`}
            id={`tabpanel-${index}`}
            role="tabpanel"
            aria-labelledby={`tab-${index}`}
            hidden={activeTab !== index}
            className={`emsa-tab-panel ${activeTab === index ? 'active' : ''}`}
          >
            {activeTab === index && tab.content}
          </div>
        ))}
      </div>
    </div>
  );
};

EMSATabsContainer.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      content: PropTypes.node.isRequired
    })
  ).isRequired,
  onTabChange: PropTypes.func
};

export default EMSATabsContainer;
